import {useEffect,useState} from 'react';
import {Check,ClipboardCheck,FileSearch,ShieldCheck} from 'lucide-react';
import {api,dateTime} from '../lib/api';

const checks=[
  ['content','Profile information reviewed','Check the visible name, biography, links and claims.'],
  ['ownership','Account and Profile ownership confirmed','Confirm the applicant controls the linked customer account and Profile.'],
  ['evidence','Evidence genuinely examined','Only mark Pass after inspecting evidence through an authorised channel.'],
  ['consistency','Profile claims supported','Compare the Profile details against the actual records examined.'],
  ['concerns','Unresolved issues addressed','Record discrepancies and request clarification where necessary.'],
  ['organisation_authority','Authority to represent the organisation confirmed','Check registration and the applicant’s right to represent it.'],
];
const sources=[
  ['account_records','Profile and account ownership records'],
  ['verified_contact','Verified account contact method'],
  ['official_register','Official company or professional register'],
  ['identity_document','Identity document (authorised secure review)'],
  ['organisation_authorisation','Organisation authorisation or business evidence'],
  ['other_independent_source','Other independently checked evidence'],
];
const initial=()=>Object.fromEntries(checks.map(([key])=>[key,'pending']));
const values=[['pending','Not checked'],['pass','Pass'],['fail','Concern'],['na','Not applicable']];
const steps=[['Profile','Inspect the submitted details'],['Evidence','Record the verification checks'],['Decision','Choose a controlled outcome']];
const entry=(key,value)=>({key,value});
function DisplayFields({fields}){
  return <dl className="profile-verification-details">{fields.filter(({value})=>value!==null&&value!==undefined&&value!=='').map(({key,value})=>
    <div key={key}><dt>{key}</dt><dd>{String(value)}</dd></div>)}</dl>;
}
export default function ProfileVerificationAssessment({referralId,onStatusChange,children}){
  const[step,setStep]=useState(0),[state,setState]=useState(null);
  const[reviewChecks,setReviewChecks]=useState(initial);
  const[evidenceSources,setEvidenceSources]=useState([]);
  const[notes,setNotes]=useState('');
  const[loading,setLoading]=useState(true),[busy,setBusy]=useState(false),[error,setError]=useState(''),[notice,setNotice]=useState('');
  useEffect(()=>{
    let active=true;
    setLoading(true);setError('');setState(null);setStep(0);
    onStatusChange({approvalReady:false});
    api('/api/back-office/referrals/'+encodeURIComponent(referralId)+'/profiles-verification/assessment')
      .then(result=>{
        if(!active)return;
        setState(result);setReviewChecks({...initial(),...(result.assessment?.checks||{})});
        setEvidenceSources(result.assessment?.evidenceSources||[]);
        setNotes(result.assessment?.notes||'');onStatusChange(result);
      })
      .catch(e=>{if(active){setError(e.message||'Verification evidence is unavailable.');onStatusChange({approvalReady:false});}})
      .finally(()=>{if(active)setLoading(false);});
    return()=>{active=false;};
  },[referralId,onStatusChange]);
  const markUnsaved=()=>{
    setNotice('');
    setState(current=>current?{...current,approvalReady:false,reason:'Save your changes before approving.'}:current);
    onStatusChange(current=>({...current,approvalReady:false,unsaved:true,reason:'Save your assessment changes before recording a decision.'}));
  };
  const save=async()=>{
    setBusy(true);setError('');setNotice('');
    try{
      const result=await api('/api/back-office/referrals/'+encodeURIComponent(referralId)+'/profiles-verification/assessment',{
        method:'POST',headers:{'content-type':'application/json'},
        body:JSON.stringify({checks:reviewChecks,evidenceSources,notes:notes.trim()})
      });
      setState(result);onStatusChange(result);
      setNotice('Assessment recorded in the case history. The final decision is a separate, controlled action.');
    }catch(e){setError(e.message||'The assessment could not be saved.');}
    finally{setBusy(false);}
  };
  const evidence=state?.evidence||{},profile=evidence.record,conflict=evidence.conflictOfInterest===true;
  const missing=evidence.availability!=='available';
  const visibleChecks=checks.filter(([key])=>key!=='organisation_authority'||profile?.kind==='organisation');
  const finished=visibleChecks.filter(([key])=>reviewChecks[key]!=='pending').length;
  const canAssess=Boolean(profile&&!conflict&&!loading);
  const setSource=(key,checked)=>{
    setEvidenceSources(current=>checked?[...new Set([...current,key])]:current.filter(item=>item!==key));
    markUnsaved();
  };

  return <div className="profile-verification-assessment">
    <div className="profile-verification-workflow-heading">
      <div><span className="profile-verification-kicker">STRUCTURED REVIEW</span>
        <h3 className="govuk-heading-s">Profile verification process</h3>
        <p className="govuk-body">Inspect the Profile, assess evidence, then record the outcome. No extra 10-character decision statement is required.</p>
      </div>
      <span className="profile-verification-step-count">Step {step+1} of 3</span>
    </div>
    <nav className="profile-verification-steps" aria-label="Verification workflow steps">
      {steps.map(([title,description],index)=>
        <button type="button" key={title} className={'profile-verification-step'+(step===index?' current':'')}
          aria-current={step===index?'step':undefined} onClick={()=>setStep(index)}>
          <span className="profile-verification-step-number">{((index===0&&profile&&evidence.availability==='available')||(index===1&&state?.approvalReady))?<Check size={15}/>:index+1}</span>
          <span><strong>{title}</strong><small>{description}</small></span>
        </button>)}
    </nav>
    {error&&<p role="alert" className="profile-verification-alert govuk-body">{error}</p>}
    {loading&&<p className="govuk-body" role="status">Retrieving the authorised Profiles snapshot…</p>}
    {state&&<div className="profile-verification-review-status" role="status">
      <strong>{conflict?'Independent reviewer required':state.approvalReady?'Assessment ready for a decision':missing?'Awaiting current Profile evidence':'Review in progress'}</strong>
      <span>{state.reason||'Record the checks and a final decision.'}</span>
    </div>}
    {step===0&&<section className="profile-verification-stage" aria-label="Profile details">
      <h3 className="govuk-heading-s"><FileSearch size={18}/>1. Profile under review</h3>
      <p className="profile-verification-stage-intro govuk-body">Check what the customer has actually published or submitted before assessing whether it deserves verification.</p>
      {state&&<div className="profile-verification-snapshot">
        <div className="profile-verification-metadata">
          <span><strong>Source</strong>{evidence.source}</span>
          <span><strong>Application date</strong>{evidence.requestedAt?dateTime(evidence.requestedAt):'Unavailable'}</span>
          <span><strong>Snapshot checked</strong>{evidence.sourceSyncedAt?dateTime(evidence.sourceSyncedAt):'Unavailable'}</span>
        </div>
        <p className="profile-verification-caution govuk-body">This is the latest available read-only Customer360 snapshot, not a historical copy of the original application. It does not provide uploaded identity documents.</p>
        {conflict&&<p className="profile-verification-alert govuk-body" role="alert"><strong>Separation of duties:</strong> This staff identity appears to belong to the applicant. Assign an independent reviewer before making any decision.</p>}
        {missing&&<p className="profile-verification-alert govuk-body" role="status">
          {profile?'The Profiles snapshot predates this application. Obtain an up-to-date record before approval.':
            'No matching authorised Profile snapshot has been received. The application cannot be approved yet.'}
        </p>}
        {profile&&<div className="profile-verification-current">
          <div className="profile-verification-profile-title">
            <div><span className="profile-verification-kicker">CUSTOMER PROFILE</span>
              <h4>{profile.displayName||profile.reference||'Profile'}</h4>
              <small>{profile.reference||evidence.profileId} · {profile.kind||'Unknown type'} · {profile.verificationStatus||'Not verified'}</small>
            </div>
            {profile.publicUrl&&<a href={profile.publicUrl} target="_blank" rel="noopener noreferrer">View public Profile ↗</a>}
          </div>
          <DisplayFields fields={[
            entry('Username',profile.username),entry('Headline',profile.headline),
            entry('Biography',profile.bio),entry('Job title',profile.jobTitle),
            entry('Organisation',profile.businessName||profile.company),
            entry('Business category',profile.businessCategory),
            entry('Public email',profile.publicEmail),entry('Public phone',profile.publicPhone),
            entry('Website',profile.website),entry('Location',profile.location),
            entry('Publication status',profile.status),
            entry('Published',profile.published?'Yes':'No'),
            entry('Suspended',profile.suspended?'Yes':'No'),
            entry('Hidden',profile.hidden?'Yes':'No'),
            entry('Profile last edited',profile.updatedAt?dateTime(profile.updatedAt):'Unknown'),
            entry('Verification requested',profile.verificationRequestedAt?dateTime(profile.verificationRequestedAt):'Unknown'),
            entry('Applicant verification note',profile.verificationRequestNote),
          ]}/>
          {evidence.modifiedSinceRequest&&<p className="profile-verification-caution govuk-body">Profile details changed after the verification request. Review the latest version and obtain the original application evidence if needed.</p>}
        </div>}
        <h4>Linked customer account</h4>
        <DisplayFields fields={[entry('Account holder',evidence.customer?.name),entry('Verified contact email',evidence.customer?.email)]}/>
        <p className="profile-verification-caution govuk-body">{state.explanation}</p>
      </div>}
      <div className="profile-verification-stage-actions">
        <button className="button primary govuk-button" type="button" onClick={()=>setStep(1)}>Continue to evidence assessment</button>
        <button className="button secondary govuk-button govuk-button--secondary" type="button" onClick={()=>setStep(2)}>Request information or record a decision</button>
      </div>
    </section>}
    {step===1&&<section className="profile-verification-stage" aria-label="Evidence assessment">
      <h3 className="govuk-heading-s"><ClipboardCheck size={18}/>2. Verification assessment checklist</h3>
      <p className="profile-verification-stage-intro govuk-body">Record the steps actually completed. Marking a box does not independently verify a customer. The approval will be held until the current Profile and required checks are available.</p>
      <h4>Evidence sources you personally examined</h4>
      <p className="profile-verification-guidance govuk-body">Select only sources genuinely checked. No documents are automatically attached to this referral.</p>
      <div className="profile-verification-sources">
        {sources.map(([key,title])=><label className="govuk-label" key={key}>
          <input type="checkbox" checked={evidenceSources.includes(key)} disabled={!canAssess||busy}
            onChange={event=>setSource(key,event.target.checked)}/>
          <span>{title}</span>
        </label>)}
      </div>
      <div className="profile-verification-checklist">
        <div className="profile-verification-checklist-progress">
          <strong>Assessment checks</strong><small>{finished} of {visibleChecks.length} recorded</small>
        </div>
        {visibleChecks.map(([key,title,helper])=><label className="profile-verification-check govuk-label" key={key}>
          <span><strong>{title}</strong><small>{helper}</small></span>
          <select className="govuk-select" aria-label={title} value={reviewChecks[key]||'pending'} disabled={!canAssess||busy}
            onChange={event=>{setReviewChecks(current=>({...current,[key]:event.target.value}));markUnsaved();}}>
            {values.map(([value,name])=><option key={value} value={value}>{name}</option>)}
          </select>
        </label>)}
        <label className="govuk-label" htmlFor="profile-verification-review-notes">Reviewer observations and evidence references <span>(optional)</span></label>
        <textarea className="govuk-textarea" id="profile-verification-review-notes" rows={4} value={notes}
          onChange={event=>{setNotes(event.target.value);markUnsaved();}}
          placeholder="What did you inspect? Include references, inconsistencies or a specific information request where relevant."
          disabled={!canAssess||busy} maxLength={4000}/>
        <p className="profile-verification-guidance govuk-body">No minimum number of characters. The evidence selections and completed checks form the structured assessment record.</p>
        <div className="profile-verification-stage-actions">
          <button className="button primary govuk-button" type="button" onClick={save} disabled={!canAssess||busy}>
            {busy?'Recording review…':'Save assessment to case history'}
          </button>
          <button className="button secondary govuk-button govuk-button--secondary" type="button" onClick={()=>setStep(2)}>Continue to decision</button>
        </div>
        {notice&&<p role="status" className="profile-verification-ready govuk-body">{notice}</p>}
        {state?.assessment&&<p className="profile-verification-guidance govuk-body">Latest assessment recorded by {state.assessment.reviewerName||'staff'} on {state.assessment.updatedAt?dateTime(state.assessment.updatedAt):'an unknown date'}.</p>}
        {state?.history?.length>0&&<details className="profile-verification-audit">
          <summary>Previous assessment entries ({state.history.length} most recent)</summary>
          {state.history.map((item,index)=><article key={item.recordedAt+'-'+index}>
            <strong>{item.reviewerName||'Staff'} · {dateTime(item.recordedAt)}</strong>
            <p className="govuk-body">{item.notes||'Structured checklist review; no additional written observations.'}</p>
            <small>{Object.entries(item.checks||{}).map(([key,value])=>key.replaceAll('_',' ')+': '+value).join(' · ')}</small>
          </article>)}
        </details>}
      </div>
    </section>}
    {step===2&&<section className="profile-verification-stage" aria-label="Verification decision">
      <h3 className="govuk-heading-s"><ShieldCheck size={18}/>3. Record the verification decision</h3>
      <p className="profile-verification-stage-intro govuk-body">Choose the correct outcome after scrutiny. Approval requires completed checks and an independent reviewer. Requesting information and rejecting remain available when evidence is insufficient.</p>
      {state?.approvalReady?<p className="profile-verification-ready govuk-body"><strong>Ready:</strong> Evidence review recorded. Approval will still require confirmation from Sousa Murray Profiles.</p>:
        <p className="profile-verification-caution govuk-body"><strong>Approval on hold:</strong> {state?.reason||'Complete and save the assessment first.'}</p>}
      <div className="profile-verification-decision-slot">{children}</div>
      <div className="profile-verification-stage-actions">
        <button className="button secondary govuk-button govuk-button--secondary" type="button" onClick={()=>setStep(0)}>Review Profile again</button>
        <button className="button secondary govuk-button govuk-button--secondary" type="button" onClick={()=>setStep(1)}>Return to assessment</button>
      </div>
    </section>}
  </div>;
}
